<template>
  <div class="home proposte" :style="tema">
    <main class="prv-main">
      <div v-if="$store.preview" class="prv-anteprima">
        <fa :icon="['fas', 'exclamation-triangle']" />
        <span>{{ labels.AnteprimaText }}</span>
      </div>

      <!-- Testata: numero del preventivo, soggiorno e nota dell'hotel -->
      <section class="prv-panel prv-testata">
        <div class="prv-intestazione">
          <span class="prv-occhiello prv-occhiello-accento">{{ etichette.Preventivo }} {{ numero }}</span>
          <h1>{{ etichette.Titolo }}</h1>
          <p>{{ etichette.Intro }}</p>
        </div>
        <div v-if="soggiorno.length" class="prv-soggiorno">
          <div v-for="voce of soggiorno" :key="voce.nome" class="prv-dato">
            <span>{{ voce.nome }}</span>
            <b>{{ voce.valore }}</b>
          </div>
        </div>
        <div v-if="note" class="prv-nota">
          <fa :icon="['far', 'sticky-note']" />
          <div>
            <b>{{ etichette.NotaHotel }}</b>
            <span>{{ note }}</span>
          </div>
        </div>
      </section>

      <!-- Proposte: il dettaglio si apre dentro la card -->
      <article v-for="card of cards" :key="card.id" :ref="'proposta-' + card.id" :class="['prv-proposta', { 'prv-proposta-scelta': card.selezionata }]">
        <div class="prv-riga">
          <div class="prv-foto" :style="{ gridTemplateRows: 'repeat(' + card.camere.length + ', minmax(130px, 1fr))' }">
            <div v-for="(camera, idx) of card.camere" :key="idx" class="prv-foto-camera">
              <img v-if="camera.immagini.length" :src="camera.immagini[0]" :alt="camera.nome" @click="ingrandisci(camera.immagini[0], camera.nome)" />
              <fa v-else class="prv-foto-vuota" :icon="['fas', 'bed']" />
              <span v-if="camera.immagini.length" class="prv-foto-conta">
                <fa :icon="['far', 'images']" />
                {{ card.multi ? camera.etichetta + " · " : "" }}{{ testo("Foto", { n: camera.immagini.length }) }}
              </span>
            </div>
          </div>

          <div class="prv-info">
            <div class="prv-codice">
              <span class="prv-occhiello">{{ labels.PropostaTitolo }} {{ card.codice }}</span>
              <span v-if="card.consigliata" class="prv-badge prv-badge-tenue">{{ etichette.Consigliata }}</span>
              <span v-if="card.selezionata" class="prv-badge">{{ etichette.Selezionata }}</span>
            </div>
            <h2>{{ card.nome }}</h2>
            <div class="prv-chips">
              <span v-if="card.trattamento" class="prv-chip prv-chip-accento"><fa :icon="['fas', 'utensils']" />{{ card.trattamento }}</span>
              <span v-if="card.tariffa" class="prv-chip"><fa :icon="['fas', 'shield-alt']" />{{ card.tariffa }}</span>
              <span class="prv-chip"><fa :icon="['fas', 'user-friends']" />{{ card.ospiti }}</span>
            </div>
            <div v-if="card.multi" class="prv-camere">
              <div v-for="(camera, idx) of card.camere" :key="idx" class="prv-camera">
                <div class="prv-camera-nome">
                  <fa :icon="['fas', 'bed']" />
                  <span><b>{{ camera.etichetta }}</b> · {{ camera.nome }}</span>
                  <span class="prv-grigio">{{ camera.ospiti }}</span>
                </div>
                <b class="prv-nowrap"><fa :icon="['fas', $theme.eurosign]" /> {{ camera.prezzo }}</b>
              </div>
            </div>
            <div v-else-if="card.camere[0].servizi.length" class="prv-servizi-breve">
              <span v-for="(servizio, idx) of card.camere[0].servizi.slice(0, servizi_breve)" :key="idx">
                <fa v-if="servizio.icon" :icon="servizio.icon" />{{ servizio.label }}
              </span>
              <span v-if="card.camere[0].servizi.length > servizi_breve">{{ testo("AltriServizi", { n: card.camere[0].servizi.length - servizi_breve }) }}</span>
            </div>
            <button type="button" class="prv-link" :aria-expanded="card.aperta ? 'true' : 'false'" @click="apri(card.id)">
              {{ card.aperta ? etichette.Nascondi : card.multi ? etichette.DettagliCamere : etichette.Dettagli }}
              <fa :class="{ 'prv-ruota': card.aperta }" :icon="['fas', 'chevron-down']" />
            </button>
          </div>

          <div class="prv-prezzo">
            <div v-if="card.sconto" class="prv-prezzo-pieno">
              <s><fa :icon="['fas', $theme.eurosign]" /> {{ card.prezzo }}</s>
              <span class="prv-badge-sconto">-{{ card.sconto }}%</span>
            </div>
            <div class="prv-prezzo-finale"><fa :icon="['fas', $theme.eurosign]" /> {{ card.prezzo_scontato }}</div>
            <div class="prv-grigio">{{ card.totale }}</div>
            <div v-if="card.confronto" class="prv-confronto">
              <template v-if="card.confronto.differenza"><fa :icon="['fas', $theme.eurosign]" /> {{ card.confronto.differenza }} </template>{{ card.confronto.testo }}
            </div>
            <button type="button" :class="['prv-scegli', { 'prv-scegli-attivo': card.selezionata }]" :aria-pressed="card.selezionata ? 'true' : 'false'" @click="scegli(card.id)">
              {{ card.selezionata ? etichette.Scelta : etichette.Scegli }}
            </button>
          </div>
        </div>

        <div v-if="card.aperta" class="prv-dettagli">
          <div v-for="(camera, idx) of card.camere" :key="idx" class="prv-dettagli-camera">
            <div v-if="camera.immagini.length" class="prv-galleria">
              <img v-for="(immagine, i) of camera.immagini" :key="i" :src="immagine" :alt="camera.nome + ', ' + (i + 1)" @click="ingrandisci(immagine, camera.nome + ', ' + (i + 1))" />
            </div>
            <div class="prv-testi">
              <div class="prv-sezione">
                <span v-if="card.multi" class="prv-occhiello">{{ camera.etichetta }} · {{ camera.ospiti }} · <fa :icon="['fas', $theme.eurosign]" /> {{ camera.prezzo }}</span>
                <h3>{{ card.multi ? camera.nome : etichette.LaCamera }}</h3>
                <p v-if="camera.descrizione">{{ camera.descrizione }}</p>
              </div>
              <div v-if="camera.servizi.length" class="prv-sezione">
                <h3>{{ etichette.ServiziCamera }}</h3>
                <div class="prv-servizi">
                  <span v-for="(servizio, i) of camera.servizi" :key="i"><fa class="prv-spunta" :icon="['fas', 'check']" />{{ servizio.label }}</span>
                </div>
              </div>
              <div v-if="camera.aggiunti.length" class="prv-sezione">
                <h3>{{ labels.ServSup2 }}</h3>
                <div class="prv-servizi">
                  <span v-for="(servizio, i) of camera.aggiunti" :key="i"><fa class="prv-spunta" :icon="['fas', 'plus']" />{{ servizio }}</span>
                </div>
              </div>
              <div v-if="camera.note" class="prv-sezione">
                <h3>{{ labels.Note }}</h3>
                <p>{{ camera.note }}</p>
              </div>
            </div>
          </div>
          <div class="prv-sezione">
            <h3>{{ etichette.TariffaCanc }}</h3>
            <p>
              <b>{{ card.tariffa }}</b> · {{ card.trattamento }}<template v-if="card.multi"> · {{ etichette.TutteCamere }}</template>
              <template v-if="card.policy"><br />{{ card.policy }}</template>
            </p>
          </div>
        </div>
      </article>

      <p v-if="cards.length" class="prv-nota-prezzi">{{ etichette.NotaPrezzi }}</p>
    </main>

    <!-- Proposta scelta: si prosegue con il form della prenotazione -->
    <div v-if="scelta" class="prv-barra">
      <div class="prv-barra-contenuto">
        <div class="prv-barra-testo">
          <span class="prv-grigio">{{ testo("HaiScelto", { proposta: scelta.codice }) }}</span>
          <b>{{ scelta.nome }}<template v-if="scelta.trattamento"> · {{ scelta.trattamento }}</template> · <span class="prv-nowrap"><fa :icon="['fas', $theme.eurosign]" /> {{ scelta.prezzo_scontato }}</span></b>
        </div>
        <button type="button" class="prv-continua" :disabled="$store.preview" @click="btnAvanti(riepilogoData(scelta.dati))">
          {{ etichette.Continua }}
          <fa :icon="['fas', 'arrow-right']" />
        </button>
      </div>
    </div>

    <!-- Foto ingrandita: un clic ovunque (o Esc) la richiude -->
    <transition name="prv-zoom">
      <div v-if="ingrandita" class="prv-zoom" @click="ingrandita = null">
        <img :src="ingrandita.src" :alt="ingrandita.alt" />
      </div>
    </transition>

    <!-- -------------------------------------------- F O O T E R -------------------------------------------- -->
    <cyb-footer :labels="labels" />
  </div>
</template>

<script>
import CybFooter from "../components/cybfooter.vue";
import apiService from "../services/api";
import formService from "../services/form";
import baseMixin from "../mixins/base";

// Lingue del Booking -> locale per le date
const LOCALI = { ita: "it", ing: "en-GB", fra: "fr", ted: "de", spa: "es", por: "pt", jap: "ja", rus: "ru" };

export default {
  components: {
    CybFooter
  },
  mixins: [baseMixin],
  data() {
    return {
      labels: { privacy: {} },
      controller: "BE_proposte",
      proposte: [],
      note: "",
      numero: "",
      // id della proposta scelta e di quella con il dettaglio aperto
      selezionata: null,
      aperta: null,
      // servizi in vista nella card chiusa, con una camera sola
      servizi_breve: 4,
      // foto ingrandita: { src, alt }
      ingrandita: null,
      multipro: []
    };
  },
  computed: {
    _lingua() {
      return this.$store.lang.toLowerCase();
    },
    etichette() {
      return this.labels.proposte || {};
    },
    // colori dell'hotel: testo su sfondo colorato e colore per il testo su bianco
    tema() {
      const colore = this.$theme.basecolor;
      const scuro = formService.perceivedBrightness(colore) <= 138;
      return {
        "--base-color": colore,
        "--base-fore": scuro ? "#ffffff" : "#000000",
        "--prv-accento-testo": scuro ? formService.brightness(colore, -28) : "#2a364d"
      };
    },
    // date e ospiti del soggiorno, dalla prima proposta
    soggiorno() {
      const prima = this.proposte[0];
      if (!prima) {
        return [];
      }
      const datepicker = this.labels.datepicker || {};
      return [
        { nome: datepicker["check-in"], valore: this.data(prima.arrivo) },
        { nome: datepicker["check-out"], valore: this.data(prima.partenza) },
        { nome: this.etichette.Durata, valore: this.notti(prima.num_notti) },
        { nome: this.etichette.Ospiti, valore: this.ospiti(prima.adulti, prima.bambini) + " · " + this.camere(prima.camere.length) }
      ];
    },
    cards() {
      const minimo = Math.min(...this.proposte.map((prop) => prop.totale));
      const prima = this.proposte[0];
      const cards = this.proposte.map((prop) => {
        const camere = prop.camere.map((cam, idx) => ({
          nome: this.tr(cam, "descBreve"),
          descrizione: this.tr(cam, "descLunga"),
          etichetta: this.labels.Room + " " + (idx + 1),
          ospiti: this.ospiti(cam.adulti, cam.bambini),
          prezzo: cam.prezzo_scontato,
          immagini: cam.immagini,
          servizi: cam.servizi.map((serv) => ({ icon: serv.icon ? serv.icon.split(" ") : null, label: this.tr(serv, "label") })),
          aggiunti: cam.servizi_aggiunti.map((serv) => this.tr(serv, "descrizione")),
          note: cam.note
        }));
        const multi = camere.length > 1;
        const date = prop.arrivo !== prima.arrivo || prop.partenza !== prima.partenza ? [this.data(prop.arrivo) + " - " + this.data(prop.partenza)] : [];
        return {
          id: prop.id,
          dati: prop,
          codice: prop.codice,
          consigliata: prop.consigliata,
          selezionata: String(this.selezionata) === String(prop.id),
          aperta: String(this.aperta) === String(prop.id),
          multi,
          camere,
          nome: camere.map((cam) => cam.nome).join(" + "),
          trattamento: this.unici(prop.camere.map((cam) => this.tr(cam, "trattamentodesc"))).join(" / "),
          tariffa: this.unici(prop.camere.map((cam) => this.tr(cam, "tariffadesc"))).join(" / "),
          policy: this.unici(prop.camere.map((cam) => this.tr(cam, "policydesc"))).join("\n\n"),
          ospiti: this.ospiti(prop.adulti, prop.bambini) + (multi ? " · " + this.camere(camere.length) : ""),
          totale: this.etichette.Totale + " · " + [...date, this.notti(prop.num_notti), ...(multi ? [this.camere(camere.length)] : []), this.ospiti(prop.adulti, prop.bambini)].join(", "),
          prezzo: prop.prezzo,
          prezzo_scontato: prop.prezzo_scontato,
          sconto: prop.sconto,
          importo: prop.totale
        };
      });

      // "€ X in piu' di ...": rispetto alla proposta piu' conveniente
      const conveniente = cards.find((card) => card.importo === minimo);
      if (cards.length > 1) {
        cards.forEach((card) => {
          if (card.importo === minimo) {
            card.confronto = { testo: this.etichette.PiuConveniente };
            return;
          }
          // spesso cambia solo il trattamento, altrimenti si nominano le camere
          const proposta = conveniente.trattamento && conveniente.trattamento !== card.trattamento ? this.minuscolo(conveniente.trattamento) : conveniente.nome;
          card.confronto = { differenza: this.prezzo(card.importo - minimo), testo: this.testo("InPiu", { proposta }) };
        });
      }
      return cards;
    },
    scelta() {
      return this.cards.find((card) => card.selezionata);
    }
  },
  mounted() {
    this.$store.lastpage = "proposte";
    window.addEventListener("keydown", this.chiudiFoto);

    this.$store.err = [];
    this.$store.db = this.$route.query.RPC_db;
    this.$store.idhotel = this.$route.query.idhotel;
    this.$store.lang = this.$route.query.lan;
    this.$store.preventivo = this.$route.query.preventivo;
    this.$store.preview = typeof this.$route.query?.preview !== "undefined";

    formService.init(this);

    this._loadHotel().then(() => {
      let promise = Promise.resolve();
      if (!this.$store.preview) {
        promise = this.notificaLinkAperto();
      }
      promise.then(() => {
        const endpoint = this.controller + "/" + this.$store.db + "/" + this.$store.idhotel + "/" + this.$store.preventivo;
        return apiService
          .get(endpoint)
          .then((resp) => {
            this.note = resp.note;
            this.numero = resp.numero;
            this.proposte = resp.proposte;

            // proposta dal link dell'e-mail (&proposta=N) o scelta prima di tornare indietro
            const iniziale = this.proposte.find((prop) => String(prop.id) === String(this.$route.query.proposta || this.$store.proposta));
            if (iniziale) {
              this.scegli(iniziale.id);
              this.aperta = iniziale.id;
              this.$nextTick(() => {
                const card = this.$refs["proposta-" + iniziale.id]?.[0];
                if (card) {
                  card.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              });
            }
          })
          .catch((err) => {
            this.$store.err = err.msg;
            this._error();
          });
      });
    });
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.chiudiFoto);
  },
  methods: {
    // :::::::::::::::::::::::::::::::: GESTIONE DATI ::::::::::::::::::::::::::::::::
    // cambia struttura
    switchHotel(idhotel) {
      window.location.href = window.location.href.replace(this.$store.idhotel, idhotel);
    },
    _loadHotel() {
      return apiService
        .get("BE_booking/" + this.$store.db + "/" + this.$store.idhotel)
        .then((resp) => {
          if (!resp.hotel) {
            this._error("ERR_HOTEL");
            return;
          }

          // dati principali hotel
          for (const item in resp.hotel) {
            if (Object.prototype.hasOwnProperty.call(resp.hotel, item)) {
              this.$hotel[item] = resp.hotel[item];
            }
          }
          this.nota_multi = resp.nota_multi;

          // colori e sfondo
          this.$theme.basecolor = resp.colore;
          this.$theme.basefore = formService.perceivedBrightness(this.$theme.basecolor) > 138 ? "#000000" : "#ffffff";
          this.$theme.eurosign = resp.eurosign;
          this.$theme.basedark = formService.brightness(this.$theme.basecolor, -50);
          this.$theme.baselight = formService.brightness(this.$theme.basecolor, 50);
          if (resp.sfondo) {
            this.$theme.background = resp.sfondo;
          }

          // bottoni multiproprietà
          this.multipro = resp.multipro;
        })
        .catch((err) => {
          for (let i = 0; i < err.msg?.length; i++) {
            this.$store.err.push(err.msg[i]);
          }
          if (this.$store.err.length > 0) {
            this._error();
          } else {
            this._error("ERR_HOTEL");
          }
        });
    },
    notificaLinkAperto() {
      return apiService.put(this.controller + "/link_aperto/" + this.$store.preventivo + "/" + this.$store.db, { idhotel: this.$store.idhotel }).catch((err) => {
        this.$store.msg = err.msg;
        this._error();
      });
    },
    // :::::::::::::::::::::::::::::::: AZIONI ::::::::::::::::::::::::::::::::
    scegli(id) {
      this.selezionata = String(this.selezionata) === String(id) ? null : id;
      this.$store.proposta = this.selezionata;
    },
    apri(id) {
      this.aperta = String(this.aperta) === String(id) ? null : id;
    },
    ingrandisci(src, alt) {
      this.ingrandita = { src, alt };
    },
    chiudiFoto(event) {
      if (event.key === "Escape") {
        this.ingrandita = null;
      }
    },
    btnAvanti(data) {
      this._goNext("/form", data);
    },
    riepilogoData(data) {
      const result = {
        periodo: {
          dataarrivo: data.arrivo,
          datapartenza: data.partenza,
          notti: data.num_notti
        },
        ospiti: data.camere.map((cam) => {
          return {
            key: cam.id,
            tipo_camera: cam.tipo_camera,
            codice: cam.codice,
            titolo: cam.descBreve_ita,
            adulti: cam.adulti,
            bambini: cam.eta_bambini,
            tariffa: {
              key: cam.tariffaid,
              tipo: cam.tariffadesc_ita,
              trattamento: cam.trattamentodesc_ita,
              prezzo: cam.prezzo_scontato,
              prezzo_base: cam.prezzo_scontato,
              policy: cam.tariffapolicy
            },
            servizi: cam.servizi_aggiunti.map((serv) => ({
              codice: serv.codice,
              tipo: serv.tipo,
              prezzo: serv.prezzo_scontato,
              titolo: serv.descrizione_ita
            }))
          };
        })
      };
      return result;
    },
    // :::::::::::::::::::::::::::::::: TESTI ::::::::::::::::::::::::::::::::
    /**
     * Etichetta della pagina con i segnaposto `%{nome}` sostituiti
     * @param {string} nome
     * @param {Object<string, string|number>} parametri
     */
    testo(nome, parametri = {}) {
      return Object.keys(parametri).reduce((testo, chiave) => testo.split("%{" + chiave + "}").join(parametri[chiave]), this.etichette[nome] || "");
    },
    // campo tradotto (`campo_ing`, ...), in italiano se la traduzione manca
    tr(obj, campo) {
      return obj[campo + "_" + this._lingua] || obj[campo + "_ita"] || "";
    },
    // a meta' frase in minuscolo, tranne in tedesco dove i nomi sono maiuscoli
    minuscolo(testo = "") {
      return this._lingua === "ted" ? testo : testo.toLowerCase();
    },
    unici(valori) {
      return [...new Set(valori.filter((valore) => !!valore))];
    },
    // data gg/mm/aaaa -> "ven 30 ott 2026" nella lingua della pagina
    data(testo) {
      const [giorno, mese, anno] = testo.split("/");
      return new Date(anno, mese - 1, giorno).toLocaleDateString(LOCALI[this._lingua] || "it", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    },
    notti(numero) {
      const datepicker = this.labels.datepicker || {};
      return numero + " " + this.minuscolo(+numero === 1 ? datepicker.night : datepicker.nights);
    },
    camere(numero) {
      return numero + " " + this.minuscolo(+numero === 1 ? this.labels.Room : this.labels.Rooms);
    },
    ospiti(adulti, bambini) {
      const testo = adulti + " " + this.minuscolo(+adulti === 1 ? this.labels.Adulto : this.labels.Adulti2);
      return +bambini > 0 ? testo + ", " + bambini + " " + this.minuscolo(+bambini === 1 ? this.labels.Bambino : this.labels.Bambini2) : testo;
    },
    // importo come i prezzi del backend: 1.234,56
    prezzo(valore) {
      return valore
        .toFixed(2)
        .replace(".", ",")
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/css/variables.scss";

$prv-grigio: #56607a;
$prv-tenue: #f3f5f8;
$prv-bordo: #edf0f4;
$prv-verde: #1f7a55;
$prv-ombra: 0 8px 30px rgba(20, 25, 40, 0.12);

.proposte {
  --prv-accento-tenue: color-mix(in srgb, var(--base-color) 10%, transparent);
  color: $text;
  // footer nel flusso, in fondo alla finestra se le proposte sono poche
  min-height: calc(100vh - 7em);
  display: flex;
  flex-direction: column;
  @media (max-width: 576px) {
    min-height: calc(100vh - 11em);
  }

  // la barra sticky non esce da .home: con il footer assoluto, 5em sotto
  // .home, a fine pagina restava staccata dal fondo
  ::v-deep .footer {
    position: static;
    margin: 0 calc(50% - 50vw);
  }

  h1,
  h2,
  h3,
  p {
    margin: 0;
  }
  button {
    font: inherit;
    cursor: pointer;
  }
}
.prv-main {
  flex: 1 0 auto;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 32px 0 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.prv-grigio {
  font-size: 13px;
  color: $prv-grigio;
}
.prv-nowrap {
  white-space: nowrap;
}
.prv-occhiello {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $prv-grigio;
}
.prv-occhiello-accento {
  letter-spacing: 0.08em;
  color: var(--prv-accento-testo);
}
.prv-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--base-color);
  color: var(--base-fore);
}
.prv-badge-tenue {
  background: var(--prv-accento-tenue);
  color: var(--prv-accento-testo);
}

// ____________________________ TESTATA ________________________________
.prv-anteprima {
  background: #fff8e6;
  border: 1px solid #e9c46a;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #6b4e00;
}
.prv-panel {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: $prv-ombra;
}
.prv-intestazione {
  display: flex;
  flex-direction: column;
  gap: 6px;
  h1 {
    font-size: 30px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  p {
    font-size: 15px;
    color: $prv-grigio;
    line-height: 1.5;
  }
}
.prv-soggiorno {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}
.prv-dato {
  background: $prv-tenue;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  span {
    font-size: 12px;
    color: $prv-grigio;
  }
  b {
    font-weight: 600;
  }
}
.prv-nota {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  background: $prv-tenue;
  font-size: 14px;
  line-height: 1.5;
  svg {
    flex: none;
    margin-top: 3px;
  }
  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
    white-space: pre-line;
  }
}

// ____________________________ PROPOSTA ________________________________
.prv-proposta {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid transparent;
  box-shadow: $prv-ombra;
  scroll-margin-top: 8em;
  &.prv-proposta-scelta {
    border-color: var(--base-color);
  }
}
.prv-riga {
  display: flex;
  flex-wrap: wrap;
}
.prv-foto {
  flex: 1 1 300px;
  max-width: 100%;
  min-height: 220px;
  display: grid;
  gap: 2px;
}
.prv-foto-camera {
  position: relative;
  min-height: 130px;
  background: #dfe3ea;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: zoom-in;
  }
}
.prv-foto-vuota {
  font-size: 40px;
  color: #ffffff;
}
.prv-foto-conta {
  position: absolute;
  left: 12px;
  bottom: 12px;
  background: rgba(20, 25, 40, 0.72);
  color: #ffffff;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.prv-info {
  flex: 999 1 340px;
  min-width: 0;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  h2 {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
  }
}
.prv-codice {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.prv-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.prv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  background: $prv-tenue;
  &.prv-chip-accento {
    font-weight: 600;
    background: var(--prv-accento-tenue);
    color: var(--prv-accento-testo);
  }
}
.prv-camere {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.prv-camera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: $prv-tenue;
  border-radius: 10px;
  font-size: 14px;
  b {
    font-weight: 600;
  }
}
.prv-camera-nome {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
  svg {
    flex: none;
  }
}
.prv-servizi-breve {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: 13px;
  color: $prv-grigio;
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
}
.prv-link {
  all: unset;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  min-height: 32px;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid $text;
    outline-offset: 2px;
  }
  svg {
    font-size: 12px;
    transition: transform 0.2s ease;
  }
  .prv-ruota {
    transform: rotate(180deg);
  }
}
.prv-prezzo {
  flex: 1 1 240px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  background: #f8f9fb;
  border-left: 1px solid $prv-bordo;
  text-align: right;
}
.prv-prezzo-pieno {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  s {
    font-size: 14px;
    color: #6b7387;
  }
}
.prv-badge-sconto {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  background: #b42318;
  padding: 2px 8px;
  border-radius: 999px;
}
.prv-prezzo-finale {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
}
.prv-confronto {
  font-size: 13px;
  font-weight: 500;
  color: $prv-verde;
}
.prv-scegli {
  margin-top: 6px;
  min-height: 48px;
  border-radius: 10px;
  border: 2px solid var(--base-color);
  background: #ffffff;
  color: var(--prv-accento-testo);
  font-size: 15px;
  font-weight: 600;
  transition: filter 0.15s ease;
  &:hover {
    filter: brightness(0.92);
  }
  &.prv-scegli-attivo {
    background: var(--base-color);
    color: var(--base-fore);
  }
}

// ____________________________ DETTAGLIO ________________________________
.prv-dettagli {
  border-top: 1px solid $prv-bordo;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.prv-dettagli-camera {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid $prv-bordo;
}
.prv-galleria {
  flex: 1 1 360px;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-content: start;
  img {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: 10px;
    cursor: zoom-in;
  }
}
.prv-testi {
  flex: 999 1 380px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.prv-sezione {
  display: flex;
  flex-direction: column;
  gap: 8px;
  h3 {
    font-size: 16px;
    font-weight: 600;
  }
  p {
    font-size: 14px;
    line-height: 1.6;
    color: #3d475e;
    white-space: pre-line;
  }
}
.prv-servizi {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 6px 16px;
  font-size: 14px;
  span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
.prv-spunta {
  color: $prv-verde;
}
.prv-nota-prezzi {
  text-align: center;
  font-size: 13px;
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

// ____________________________ FOTO INGRANDITA ________________________________
.prv-zoom {
  position: fixed;
  inset: 0;
  z-index: 100;
  padding: 24px;
  background: rgba(10, 12, 20, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
}
.prv-zoom-enter-active,
.prv-zoom-leave-active {
  transition: opacity 0.2s;
}
.prv-zoom-enter,
.prv-zoom-leave-to {
  opacity: 0;
}

// ____________________________ BARRA DELLA SCELTA ________________________________
.prv-barra {
  position: sticky;
  bottom: 0;
  z-index: 15;
  // a tutta larghezza, fuori dai margini di .home
  margin: 0 calc(50% - 50vw);
  background: rgba(255, 255, 255, 0.97);
  border-top: 3px solid var(--base-color);
  box-shadow: 0 -8px 24px rgba(20, 25, 40, 0.14);
}
.prv-barra-contenuto {
  max-width: 1180px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 14px 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
}
.prv-barra-testo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  b {
    font-size: 16px;
    font-weight: 600;
  }
}
.prv-continua {
  min-height: 48px;
  padding: 0 28px;
  border: none;
  border-radius: 10px;
  background: var(--base-color);
  color: var(--base-fore);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  transition: filter 0.15s ease;
  &:hover {
    filter: brightness(0.92);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    filter: none;
  }
}
</style>
